import {
    EnvNameWrapper,
    IndentColumn,
    LogoColumn,
    NavBarExternalLink,
    NavBarStyles,
    NavColumn,
    NavContainer,
    NavLinksGroup,
    NavRow,
    NavWrapper,
    UserProfileMenuWrapper,
} from "./NavBar.styles";
import React, { useContext, useState } from "react";

import AboutModal from "shared/components/feedback/modals/AboutModal";
import { BREAKPOINT_VALUES } from "shared/config/breakpoints";
import GenericModal from "shared/components/feedback/modals/common/GenericModal";
import Link from "@material-ui/core/Link";
import Logo from "shared/components/utils/icons/Logo";
import NavGroupMenu from "shared/components/navigation/links/NavGroupMenu";
import NavLink from "shared/components/navigation/links/NavLink";
import ReactDOM from "react-dom";
import SessionContext from "app/contexts/SessionContext";
import Text from "shared/components/data-display/mui/Text";
import UserProfileMenu from "./UserProfileMenu";
import { buildNavGroups } from "shared/config/navGroups";
import env from "react-dotenv";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { useNavigationLinks } from "shared/hooks/access/useNavigationLinks";
import { useStylesForText } from "shared/styles/makeStyles";

const NavBar = () => {
    const [hideProfileMenu, setHideProfileMenu] = useState(false);
    const classes = NavBarStyles();
    const classesText = useStylesForText();

    const session = useContext(SessionContext);

    // Fetches the nav items for the current user from the backend.
    // Visibility of each item is permission-controlled server-side.
    const { nav, loading } = useNavigationLinks(
        session.legacyId,
        session.currentDepartmentId
    );

    const navigate = useNavigate();

    // At or below NavCollapse the flat link row gets too cramped, so we collapse
    // the links into grouped dropdowns instead. Single source of truth: breakpoints.js.
    const collapsed = useMediaQuery(
        `(max-width: ${BREAKPOINT_VALUES.NavCollapse})`
    );

    // Hardcoded external links (not part of the backend navigation payload).
    const reporterHref =
        "/redirectto?url=" +
        env.BESTWORK_REACT_REPORTER10_LINK +
        "&env=" +
        env.ENVIRONMENT_NAME +
        "&vpn=true";

    const feedbackHref =
        "/redirectto?url=" +
        env.BESTWORK_REACT_FEEDBACK_FORM_URL +
        "&env=" +
        env.ENVIRONMENT_NAME;

    const externalLinks = [
        {
            kind: "external",
            id: "reporter",
            name: "Reporter",
            href: reporterHref,
            rel: "noreferrer",
        },
        {
            kind: "external",
            id: "feedback",
            name: "Provide Feedback",
            href: feedbackHref,
            target: "_blank",
            rel: "noreferrer",
        },
    ];

    // Backend-driven links (route/name/module), minus the Home ("/") entry.
    const internalLinks = nav
        .filter((item) => item.route !== "/")
        .map((item) => ({
            kind: "internal",
            id: item.route,
            route: item.route,
            name: item.name,
            module: item.module,
        }));

    const navGroups = buildNavGroups(internalLinks, externalLinks);

    // Returns a custom isActive function for nav items that require non-default active state logic.
    // - "My Allocations" (/my-allocations): active when on /allocations with the user's own userId in the URL.
    // - "Allocations" (/allocations): active when on /allocations with a different (or no) userId in the URL.
    //   This prevents both items from being underlined at the same time.
    // - All other routes: returns undefined, falling back to the default startsWith behavior in NavLink.
    const getNavLinkIsActive = (route) => {
        const { myAllocations, allocation } = globalThis.config.routes;
        const myLegacyId = String(session.legacyId);

        if (route === myAllocations) {
            return (location) => {
                const userId = new URLSearchParams(location.search).get(
                    "userId"
                );
                return (
                    location.pathname === allocation && userId === myLegacyId
                );
            };
        }

        if (route === allocation) {
            return (location) => {
                const userId = new URLSearchParams(location.search).get(
                    "userId"
                );
                return (
                    location.pathname === allocation && userId !== myLegacyId
                );
            };
        }

        return undefined;
    };

    const handleAboutShow = async () => {
        const modalContent = <AboutModal></AboutModal>;
        const modal = (
            <GenericModal
                content={modalContent}
                parent="divPageModal"
                title="Version Information"
            />
        );
        ReactDOM.render(modal, document.getElementById("divPageModal"));
        setHideProfileMenu(true);
    };

    return (
        <NavWrapper id="top-nav">
            <NavContainer fluid>
                <NavRow>
                    <>
                        <IndentColumn xs={1} />
                        <LogoColumn xs={2}>
                            <Link
                                component="button"
                                className={classes.logo}
                                onClick={() => {
                                    globalThis.location.href = `${globalThis.location.href.split("?")[0]}`;
                                }}
                            >
                                <Logo />
                            </Link>

                            {env.ENVIRONMENT_NAME && (
                                <EnvNameWrapper>
                                    {" "}
                                    {env.ENVIRONMENT_NAME}
                                </EnvNameWrapper>
                            )}
                        </LogoColumn>
                        <NavColumn>
                            <NavLinksGroup>
                                {!loading &&
                                    !collapsed &&
                                    internalLinks.map((item) => (
                                        <NavLink
                                            key={item.route}
                                            item={item}
                                            classes={classes}
                                            classesText={classesText}
                                            navigate={navigate}
                                            isActive={getNavLinkIsActive(
                                                item.route
                                            )}
                                        />
                                    ))}
                                {!loading &&
                                    !collapsed &&
                                    externalLinks.map((link) => (
                                        <NavBarExternalLink
                                            key={link.id}
                                            href={link.href}
                                            target={link.target}
                                            rel={link.rel}
                                        >
                                            <Text
                                                variant="smallLight"
                                                style={{ width: "fit-content" }}
                                                className={
                                                    classesText.borderBottom
                                                }
                                            >
                                                {link.name}
                                            </Text>
                                        </NavBarExternalLink>
                                    ))}
                                {!loading &&
                                    collapsed &&
                                    navGroups.map((group) => (
                                        // Always render the dropdown, even for a
                                        // single visible item. Empty groups are
                                        // already dropped by buildNavGroups.
                                        // Empty groups are not shown.
                                        <NavGroupMenu
                                            key={group.key}
                                            group={group}
                                            classesText={classesText}
                                            navigate={navigate}
                                            getIsActive={getNavLinkIsActive}
                                        />
                                    ))}
                            </NavLinksGroup>
                            <UserProfileMenuWrapper>
                                <UserProfileMenu
                                    hide={hideProfileMenu}
                                    onAboutItemClick={handleAboutShow}
                                />
                            </UserProfileMenuWrapper>
                        </NavColumn>
                    </>
                </NavRow>
            </NavContainer>
        </NavWrapper>
    );
};

NavBar.propTypes = {};

export default NavBar;
